import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Home, Phone, Building2, ShieldCheck, Bell, AlertTriangle, MessageSquare, Heart, FileText, CheckCircle, Clipboard } from 'lucide-react';
import { getNotificationTarget } from '../lib/notificationTargets';
import { dismissUpgradePrompt, getUpgradePromptsForPlacement } from '../lib/upgradeHints';

export const Navbar = () => {
  const { user, logout, isAuthenticated, isTenant, isAdmin, isLandlord } = useAuth();
  const { loading: siteSettingsLoading, publicDocumentDispatchEnabled } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [dismissedHintVersion, setDismissedHintVersion] = useState(0);
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, {
        withCredentials: true,
      });
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (_) {}
  };

  useEffect(() => {
    if (isAuthenticated()) fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated()) {
      setNotifications([]);
      return;
    }
    const intervalId = setInterval(fetchNotifications, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (notificationsOpen && isAuthenticated()) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsOpen]);

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const navbarUpgradeHint = isLandlord()
    ? getUpgradePromptsForPlacement(user?.metrics, 'navbar')[0]
    : null;

  const handleDismissUpgradeHint = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!navbarUpgradeHint?.id) return;
    dismissUpgradePrompt(navbarUpgradeHint.id);
    setDismissedHintVersion((value) => value + 1);
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, {
        withCredentials: true,
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (_) {}
  };

  const handleDeleteNotification = async (id, e) => {
    e?.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`, {
        withCredentials: true,
      });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, {
        withCredentials: true,
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (_) {}
  };

  const handleOpenNotificationTarget = async (notif) => {
    if (!notif?.id) return;

    if (!notif.is_read) {
      await handleMarkNotificationRead(notif.id);
    }

    const target = getNotificationTarget(notif, user?.role);
    setNotificationsOpen(false);
    navigate(
      {
        pathname: target.pathname,
        search: target.search || '',
        hash: target.hash || '',
      },
      target.state ? { state: target.state } : undefined,
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'admin':
        return '/admin';
      case 'landlord':
        return '/landlord';
      case 'tenant':
        return '/tenant';
      default:
        return '/';
    }
  };

  // Different nav links based on auth status and role
  const getNavLinks = () => {
    const links = [
      { href: '/', label: 'Home', icon: Home },
    ];
    
    if (isAuthenticated() && isTenant()) {
      links.push({ href: '/tenant', label: 'My Tenancy', icon: LayoutDashboard });
      links.push({ href: '/tenant/explore', label: 'Explore', icon: Building2 });
    }
    
    if (isAuthenticated() && isLandlord()) {
      links.push({ href: '/landlord', label: 'Dashboard', icon: Building2 });
    }
    
    if (isAuthenticated() && isAdmin()) {
      links.push({ href: '/admin', label: 'Admin', icon: ShieldCheck });
    }

    links.push({ href: '/contact', label: 'Contact', icon: Phone });
    const canShowSendDocumentsLink = publicDocumentDispatchEnabled
      ? (!isAuthenticated() || !isTenant())
      : (isAuthenticated() && (isLandlord() || isAdmin()));

    if (!siteSettingsLoading && canShowSendDocumentsLink) {
      links.push({ href: '/send-documents', label: 'Send Documents', icon: FileText });
    }
    
    return links;
  };

  const navLinks = getNavLinks();
  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/brand/logo-header.png" 
              alt="SIYAK"
              className="h-8 w-auto shrink-0 bg-transparent md:h-10"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(({ href, label }) => (
              <Link
                key={href}
                to={href}
                className={`relative text-sm font-medium transition-colors hover:text-royal-600 ${
                  isActive(href) ? 'text-royal-600' : 'text-navy-600'
                }`}
                data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
              >
                {label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth + Notifications */}
          <div className="hidden md:flex items-center gap-3">
            {navbarUpgradeHint ? (
              <div key={dismissedHintVersion} className="hidden xl:flex items-center gap-2">
                <Link
                  to="/settings/billing"
                  className="max-w-[320px] rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  title={navbarUpgradeHint.message}
                >
                  {navbarUpgradeHint.message}
                </Link>
                <button
                  type="button"
                  aria-label="Dismiss upgrade hint"
                  className="rounded-full p-1 text-amber-800 hover:bg-amber-100"
                  onClick={handleDismissUpgradeHint}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
            {isAuthenticated() && (
              <button
                onClick={() => setNotificationsOpen(true)}
                className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Notifications"
                data-testid="navbar-notifications-btn"
              >
                <Bell className="h-5 w-5 text-navy-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-semibold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            {isAuthenticated() ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="h-8 w-8 rounded-full bg-royal-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-royal-600" />
                    </div>
                    <span className="text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <p className="text-xs text-royal-600 capitalize mt-1">{user?.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-link">
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="account-settings-link">
                      <User className="h-4 w-4" />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" asChild data-testid="login-nav-btn">
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild data-testid="register-nav-btn">
                  <Link to="/register">Register</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map(({ href, label, icon: Icon }) => (
                <Link
                  key={href}
                  to={href}
                  className={`flex items-center gap-2 px-2 py-2 rounded-md transition-colors ${
                    isActive(href) ? 'bg-royal-50 text-royal-600' : 'text-navy-600 hover:bg-navy-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </Link>
              ))}
              
              {isAuthenticated() && (
                <button
                  onClick={() => { setNotificationsOpen(true); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-navy-50 w-full text-left"
                  data-testid="mobile-notifications-btn"
                >
                  <div className="relative">
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 h-3 w-3 rounded-full bg-red-500" />
                    )}
                  </div>
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <Badge variant="destructive" className="ml-auto text-xs h-5">{unreadCount}</Badge>
                  )}
                </button>
              )}
              <div className="border-t my-2 pt-2">
                {isAuthenticated() ? (
                  <>
                    <div className="px-2 py-2 text-sm">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      <p className="text-xs text-royal-600 capitalize">{user?.role}</p>
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-navy-100 w-full text-left"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User className="h-4 w-4" />
                      Account Settings
                    </Link>
                    {navbarUpgradeHint ? (
                      <div key={`mobile-${dismissedHintVersion}`} className="flex items-center gap-2">
                        <Link
                          to="/settings/billing"
                          className="flex-1 rounded-md bg-amber-50 px-2 py-2 text-left text-amber-900 hover:bg-amber-100"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="text-xs font-medium">{navbarUpgradeHint.message}</span>
                        </Link>
                        <button
                          type="button"
                          aria-label="Dismiss upgrade hint"
                          className="rounded-md p-2 text-amber-900 hover:bg-amber-100"
                          onClick={handleDismissUpgradeHint}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : null}
                    <button
                      onClick={async () => {
                        await handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 px-2 py-2 rounded-md text-destructive hover:bg-red-50 w-full text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="flex items-center justify-center px-4 py-2 rounded-md border border-navy-200 text-navy-600 hover:bg-navy-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="flex items-center justify-center px-4 py-2 mt-2 rounded-md bg-navy-900 text-white hover:bg-navy-800"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Register
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notifications Dialog */}
      <Dialog open={notificationsOpen} onOpenChange={setNotificationsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Centre {unreadCount > 0 && `(${unreadCount} unread)`}
            </DialogTitle>
          </DialogHeader>
          {notifications.length > 0 ? (
            <div className="space-y-3">
              {unreadCount > 0 && (
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-royal-600 hover:text-royal-700">
                    Mark all read
                  </Button>
                </div>
              )}
              {notifications.map((notif) => {
                const type = notif.notification_type || notif.type;
                let Icon = Bell;
                let iconColor = 'text-navy-600';
                let bgColor = 'bg-navy-100';
                if (type === 'issue_reported') { Icon = AlertTriangle; iconColor = 'text-yellow-600'; bgColor = 'bg-yellow-100'; }
                else if (type === 'application_received') { Icon = Clipboard; iconColor = 'text-blue-600'; bgColor = 'bg-blue-100'; }
                else if (type === 'property_saved') { Icon = Heart; iconColor = 'text-red-600'; bgColor = 'bg-red-100'; }
                else if (type === 'transfer_request') { Icon = MessageSquare; iconColor = 'text-orange-600'; bgColor = 'bg-orange-100'; }
                else if (type === 'status_changed' || type === 'note_added') { Icon = CheckCircle; iconColor = 'text-green-600'; bgColor = 'bg-green-100'; }
                else if (type === 'document_uploaded' || type === 'document_deleted') { Icon = FileText; iconColor = 'text-purple-600'; bgColor = 'bg-purple-100'; }
                return (
                  <Card
                    key={notif.id}
                    className={`cursor-pointer transition-colors hover:bg-slate-50 ${!notif.is_read ? 'border-royal-200 bg-royal-50/50' : ''}`}
                    onClick={() => handleOpenNotificationTarget(notif)}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon className={`h-4 w-4 ${iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm leading-snug min-w-0 break-words">
                              {notif.title}
                              {!notif.is_read && (
                                <Badge className="ml-2 bg-royal-100 text-royal-700 text-xs align-middle">New</Badge>
                              )}
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDeleteNotification(notif.id, e)}
                              className="h-7 w-7 p-0 hover:text-destructive flex-shrink-0 -mt-0.5"
                            >
                              ×
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5 break-words">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString('en-GB')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium">No Notifications</h3>
              <p className="text-sm text-muted-foreground">You're all caught up!</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </nav>
  );
};
